// Chart of accounts empty state.
// Illustration + template picker (3 templates with outlined-circle icons) + Create button
// + outlined "Start blank" button. Start blank opens the New account drawer.

import React from "react";
import { Building2, Stethoscope, Package, ArrowRight } from "lucide-react";
import { TEMPLATES } from "../constants";
import illustration from "../../../../assets/illustrations/Businessman_balancing_on_time_unicycle.svg";

interface EmptyStateProps {
  selectedTemplateId: string;
  onSelectTemplate: (id: string) => void;
  onCreateFromTemplate: () => void;
  onStartBlank: () => void;
}

const ICONS: Record<string, React.ElementType> = {
  Building2,
  Stethoscope,
  Package,
};

export default function EmptyState({ selectedTemplateId, onSelectTemplate, onCreateFromTemplate, onStartBlank }: EmptyStateProps) {
  const selected = TEMPLATES.find((t) => t.id === selectedTemplateId);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 md:py-10 text-center">
      <img
        src={illustration}
        alt=""
        aria-hidden="true"
        className="max-w-[280px] md:max-w-[300px] mx-auto mb-6 md:mb-8 block"
      />
      <h2 className="text-xl md:text-2xl font-extrabold text-ink mb-2 -tracking-[0.015em]">
        Set up your chart of accounts
      </h2>
      <p className="text-[13px] md:text-[14px] text-ink3 max-w-xl mx-auto mb-8 leading-relaxed">
        Pick a template tuned to your industry, or start blank and add accounts as you go. Vela keeps your chart tidy as transactions come in.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-3.5 text-left mb-6">
        {TEMPLATES.map((t) => {
          const Icon = ICONS[t.icon] ?? Building2;
          const isPicked = t.id === selectedTemplateId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelectTemplate(t.id)}
              className={`relative text-left bg-white rounded-xl p-4 md:p-5 border transition cursor-pointer hover:border-ink3 hover:shadow-[0_2px_8px_rgba(14,26,26,0.04)] ${
                isPicked ? "border-teal-dark border-2 p-[15px] md:p-[19px]" : "border-line"
              }`}
            >
              {t.recommended && (
                <span className="absolute -top-2 right-3.5 bg-teal-dark text-white text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full">
                  Recommended
                </span>
              )}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
                  isPicked ? "border-2 border-teal-dark text-teal-dark" : "border border-line text-ink3"
                }`}
              >
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <h4 className="text-[13.5px] md:text-[14px] font-bold text-ink m-0 mb-1 leading-tight">{t.name}</h4>
              <p className="text-[11.5px] md:text-[12px] text-ink3 m-0 mb-2.5 leading-snug">{t.description}</p>
              <div className="text-[11.5px] text-ink2 font-bold">{t.accountCount} accounts</div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={onCreateFromTemplate}
          className="inline-flex items-center justify-center gap-2 bg-teal-dark text-white text-[13.5px] md:text-[14px] font-bold px-6 py-3 rounded-xl border-none cursor-pointer hover:brightness-110"
        >
          Create {selected?.accountCount ?? 42} accounts from {selected?.name ?? "Healthcare"} template
        </button>
        <button
          type="button"
          onClick={onStartBlank}
          className="inline-flex items-center gap-1.5 bg-transparent text-teal-dark text-[13px] font-bold border border-line rounded-xl px-5 py-2.5 cursor-pointer hover:bg-sage hover:border-teal-dark transition"
        >
          Start blank, add accounts one at a time
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}